import * as React from "react"
import { finder } from "@medv/finder"

interface IProps {
  onPick: (selector: string, element: Element) => void
  onCancel: () => void
  onHover: (selector: string | null) => void
}

const HIGHLIGHT_ID = "bizzey-tour-builder-highlight"

// Lives in light DOM (outside the shadow root), styled entirely with inline
// styles rather than classes, so it can visually sit on top of arbitrary
// host-page content with zero risk of colliding with either the host's CSS
// or our own scoped shadow-root styles.
const ElementHighlighter = ({ onPick, onCancel, onHover }: IProps) => {
  React.useEffect(() => {
    const box = document.createElement("div")
    box.id = HIGHLIGHT_ID
    box.style.cssText = [
      "position: fixed",
      "pointer-events: none",
      "z-index: 2147483001",
      "border: 2px solid #22c55e",
      "background: rgba(34, 197, 94, 0.08)",
      "border-radius: 4px",
      "display: none",
    ].join(";")
    document.body.appendChild(box)

    const isOwnUi = (el: Element | null) =>
      !!el && el.closest("#bizzey-tour-builder-host") !== null

    let lastTarget: Element | null = null

    const onMouseMove = (e: MouseEvent) => {
      const target = document.elementFromPoint(e.clientX, e.clientY)
      if (!target || isOwnUi(target)) {
        box.style.display = "none"
        if (lastTarget !== null) {
          lastTarget = null
          onHover(null)
        }
        return
      }

      const rect = target.getBoundingClientRect()
      box.style.display = "block"
      box.style.top = `${rect.top}px`
      box.style.left = `${rect.left}px`
      box.style.width = `${rect.width}px`
      box.style.height = `${rect.height}px`

      if (target !== lastTarget) {
        lastTarget = target
        onHover(finder(target, { seedMinLength: 3, optimizedMinLength: 2 }))
      }
    }

    const onClick = (e: MouseEvent) => {
      const target = document.elementFromPoint(e.clientX, e.clientY)
      if (!target || isOwnUi(target)) return

      e.preventDefault()
      e.stopPropagation()

      const selector = finder(target, { seedMinLength: 3, optimizedMinLength: 2 })
      onPick(selector, target)
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel()
    }

    document.addEventListener("mousemove", onMouseMove, true)
    document.addEventListener("click", onClick, true)
    document.addEventListener("keydown", onKeyDown, true)
    const previousCursor = document.body.style.cursor
    document.body.style.cursor = "crosshair"

    return () => {
      document.removeEventListener("mousemove", onMouseMove, true)
      document.removeEventListener("click", onClick, true)
      document.removeEventListener("keydown", onKeyDown, true)
      document.body.style.cursor = previousCursor
      box.remove()
    }
  }, [onPick, onCancel, onHover])

  return null
}

export default ElementHighlighter
